import { useState } from 'react';
import { useModal, useDialog } from '@intsoft/innocode-core';
import { PasswordChangeModal } from '../../../resources/modal/PasswordChangeModal';
import { ZENPAY_PAY_PASSWORD } from '../../../utils/Config';
import axios from "../../../utils/api/axios";
import { getCookie } from '../../../utils/cookies/cookies';

// JSDoc을 사용하여 타입을 정의합니다.
/**
 * @typedef {'verify' | 'modify' | 'create'} ModalMode
 */

/**
 * @typedef {object} OpenModalOptions
 * @property {ModalMode} [selectMode]
 * @property {(password?: string) => void} [onSuccess]
 * @property {string} [userId]
 */

export const usePasswordChangeModal = (userId) => {
    const { showInnocodeModal: showBottomModal } = useModal();
    const { showDialog } = useDialog();
    const [isLoading, setIsLoading] = useState(false);


    const checkPayPasswordStatus = async (targetUserId) => {
        try {
            const _userId = targetUserId || userId || getCookie('user_id');

            const response = await axios.get(`${ZENPAY_PAY_PASSWORD}/status/${_userId}`);
            console.log('response', response.data);
            return response.data.data;
        } catch (error) {
            console.error('비밀번호 상태 확인 중 오류 발생:', error);
            return false;
        }
    };

    /**
     * 비밀번호 상태 확인 후 적절한 모드로 모달을 여는 핸들러 함수
     * @param {OpenModalOptions} options
     */
    const handleOpenModal = async (options = {}) => {
        // 이미 로딩 중이면 중복 실행 방지
        if (isLoading) return;
        setIsLoading(true);

        try {
            // API를 호출하여 비밀번호 존재 여부를 확인
            const { selectMode, onSuccess, userId: targetUserId } = options;

            const hasPassword = await checkPayPasswordStatus(targetUserId);
            let titleMsg = ''

            /** @type {ModalMode} */
            let finalMode;

            if (!hasPassword) { //비밀번호가 없으면 true
                // 비밀번호가 없으면 어떤 요청이든 'create' 모드로 강제합니다.
                finalMode = 'create';
            } else if (selectMode) {
                // 비밀번호가 있고, 원하는 모드(selectMode)가 지정되었다면 그 모드를 따릅니다.
                finalMode = selectMode;
            } else {
                // 비밀번호가 있지만, 원하는 모드가 없으면 기본값인 'modify'로 설정합니다.
                finalMode = 'modify';
            }

            console.log('finalMode', finalMode);
            // 4. 모드에 따른 제목을 관리하는 맵 (유지보수 용이)
            const successTitles = {
                create: '결제 비밀번호가\n생성되었습니다.',
                modify: '결제 비밀번호가\n변경되었습니다.',
                verify: '결제 비밀번호가\n확인되었습니다.',
            };

            // 여기서부터는 이전 로직과 유사
            showBottomModal({
                enableDefaultStyle: true,
                headerBuilder: () => <></>,
                contentBuilder: (
                    _data,
                    _handleSave,
                    _handleDelete,
                    _handleUpdate,
                    handleClose
                ) => {
                    const handlePasswordSubmit = (password) => {
                        handleClose();
                        showDialog({
                            type: 'info',
                            iconType: 'check',
                            title: successTitles[finalMode],
                            confirmText: '완료',
                            onConfirm: () => {
                                // 성공 시 콜백이 있다면 실행
                                if (options?.onSuccess) {
                                    options.onSuccess();
                                } else {
                                    window.history.back()
                                }

                            }
                        });
                    };

                    return (
                        <PasswordChangeModal
                            isOpen={true}
                            onClose={handleClose}
                            mode={finalMode} // 동적으로 결정된 mode를 전달
                            onPasswordSubmit={handlePasswordSubmit}
                            userId={targetUserId}
                        />
                    );
                },
            });

        } catch (error) {
            // API 호출 실패 시 에러 다이얼로그 표시
            showDialog({
                type: 'error',
                title: '오류 발생',
                content: '비밀번호 정보를 가져오는 데 실패했습니다. 잠시 후 다시 시도해주세요.',
                confirmText: '확인'
            });
        } finally {
            // 성공/실패 여부와 관계없이 로딩 상태 해제
            setIsLoading(false);
        }
    };

    // 컴포넌트에서는 handleOpenModal 함수와 isLoading 상태를 사용합니다.
    return { handleOpenModal, isLoading };
};